function finiteCoordinate(value) {
    if (value == null || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function coordinatesMatch(latA, lngA, latB, lngB) {
    const aLat = finiteCoordinate(latA);
    const aLng = finiteCoordinate(lngA);
    const bLat = finiteCoordinate(latB);
    const bLng = finiteCoordinate(lngB);
    const aKnown = aLat != null && aLng != null;
    const bKnown = bLat != null && bLng != null;

    if (!aKnown || !bKnown) {
        return !aKnown && !bKnown;
    }

    return Math.abs(aLat - bLat) + Math.abs(aLng - bLng) < 0.0001;
}

function isStopPending(value) {
    if (value == null || value === '') {
        return true;
    }

    return Number(value) === 0;
}

function samePendingOrDone(left, right) {
    return isStopPending(left) === isStopPending(right);
}

function readSuite(source) {
    if (!source || typeof source !== 'object') {
        return '';
    }
    const raw = source.suite ?? source.appartment ?? source.SUITE ?? source.apartment ?? '';
    return String(raw ?? '').trim();
}

function stepSuite(step) {
    const own = readSuite(step) || readSuite(step?.destination);
    if (own) {
        return own;
    }

    const suites = [];
    for (const order of step?.orders ?? []) {
        const value = readSuite(order) || readSuite(order?.destination);
        if (value) {
            suites.push(value);
        }
    }

    return [...new Set(suites)].sort().join('\u0000');
}

function normalizePlaceText(value) {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
}

function stepCity(step) {
    const raw = step?.city
        ?? step?.orders?.[0]?.city
        ?? step?.orders?.[0]?.destination?.city
        ?? '';
    return normalizePlaceText(raw);
}

function citiesCompatible(previous, current) {
    const left = stepCity(previous);
    const right = stepCity(current);
    if (left && right) {
        return left === right;
    }

    const coordsKnown = finiteCoordinate(previous?.latitude) != null
        && finiteCoordinate(previous?.longitude) != null
        && finiteCoordinate(current?.latitude) != null
        && finiteCoordinate(current?.longitude) != null;
    if (coordsKnown) {
        return true;
    }

    return left === right;
}

function stepDestid(step) {
    return step?.DESTID ?? step?.orders?.[0]?.DESTID ?? null;
}

function isPickupStep(step) {
    return step?.is_pickup == 1;
}

function earlierTimestamp(left, right) {
    if (left == null || left === '') {
        return right == null || right === '' ? left : right;
    }
    if (right == null || right === '') {
        return left;
    }

    const leftTime = new Date(left).getTime();
    const rightTime = new Date(right).getTime();
    if (!Number.isFinite(leftTime)) {
        return right;
    }
    if (!Number.isFinite(rightTime)) {
        return left;
    }

    return leftTime <= rightTime ? left : right;
}

function mergeComment(left, right) {
    const a = String(left ?? '').trim();
    const b = String(right ?? '').trim();
    if (!a) {
        return b ? right : left;
    }
    if (!b || a === b) {
        return left;
    }

    return `${a} | ${b}`;
}

function mergedDoneFlag(existingValue, incomingValue) {
    if (existingValue == 1 && incomingValue == 1) {
        return 1;
    }
    if (existingValue == null && incomingValue == null) {
        return null;
    }

    return 0;
}

function countPackages(step) {
    const orders = Array.isArray(step?.orders) ? step.orders : [];
    const counted = orders.filter((order) => order?.package_count != null && order.package_count !== '');
    if (counted.length > 0) {
        return counted.reduce((sum, order) => {
            const count = Number(order.package_count);
            return sum + (Number.isFinite(count) ? count : 0);
        }, 0);
    }

    const stepCount = Number(step?.package_count);
    if (Number.isFinite(stepCount) && stepCount > 0) {
        return stepCount;
    }

    return Math.max(orders.length, 1);
}

function mergedStepFields(existing, current) {
    const fields = {
        deadline: earlierTimestamp(existing.deadline, current.deadline),
        scheduled_deadline: earlierTimestamp(existing.scheduled_deadline, current.scheduled_deadline),
        comment: mergeComment(existing.comment, current.comment),
    };
    if (current.use_pod) {
        fields.use_pod = current.use_pod;
    }
    if (current.is_suggested_new_stop) {
        fields.is_suggested_new_stop = true;
    }
    if (current.is_current_destination) {
        fields.is_current_destination = true;
    }
    if (existing.is_locked || current.is_locked) {
        fields.is_locked = true;
    }

    return fields;
}

function sameContext(previous, current) {
    return Boolean(previous?.is_completed_context) === Boolean(current?.is_completed_context);
}

function canMergePickup(previous, current) {
    if (!isPickupStep(previous) || !isPickupStep(current) || !sameContext(previous, current)) {
        return false;
    }
    if (current.name != previous.name || current.address != previous.address) {
        return false;
    }
    if (!coordinatesMatch(current.latitude, current.longitude, previous.latitude, previous.longitude)) {
        return false;
    }

    // Missing, null, and 0 are all pending, so a newly inserted stop merges
    // with an existing pending one. A completed pickup stays on its own row.
    return samePendingOrDone(current.PICKUP_IS_DONE, previous.PICKUP_IS_DONE);
}

function canMergeDelivery(previous, current) {
    if (isPickupStep(previous) || isPickupStep(current) || !sameContext(previous, current)) {
        return false;
    }
    if (!samePendingOrDone(current.DELIVERY_IS_DONE, previous.DELIVERY_IS_DONE)) {
        return false;
    }

    const currentDestid = stepDestid(current);
    const previousDestid = stepDestid(previous);
    const sameDestid = currentDestid != null
        && currentDestid !== ''
        && previousDestid != null
        && Number(currentDestid) === Number(previousDestid);

    const currentName = String(current.name ?? '').trim();
    const currentAddress = String(current.address ?? '').trim();
    const sameDeliveryPlace = currentName !== ''
        && currentName === String(previous.name ?? '').trim()
        && currentAddress !== ''
        && currentAddress === String(previous.address ?? '').trim()
        && coordinatesMatch(current.latitude, current.longitude, previous.latitude, previous.longitude)
        && stepSuite(current) === stepSuite(previous)
        && citiesCompatible(previous, current);

    return sameDestid || sameDeliveryPlace;
}

export function filterSteps(steps) {
    const newSteps = [];
    let previous = null;

    for (let i = 0; i < steps?.length; i++) {
        const currentStep = steps[i];
        if (!currentStep || typeof currentStep !== 'object') {
            continue;
        }

        const mergePickup = previous != null && canMergePickup(previous, currentStep);
        const mergeDelivery = previous != null && !mergePickup && canMergeDelivery(previous, currentStep);

        if (!mergePickup && !mergeDelivery) {
            newSteps.push(currentStep);
            previous = currentStep;
            continue;
        }

        const existing = newSteps[newSteps.length - 1];
        if (!existing) {
            newSteps.push(currentStep);
            previous = currentStep;
            continue;
        }

        const mergedOrders = [...(existing.orders ?? []), ...(currentStep.orders ?? [])];
        if (mergePickup) {
            newSteps[newSteps.length - 1] = {
                ...existing,
                orders: mergedOrders,
                PICKUP_IS_DONE: mergedDoneFlag(existing.PICKUP_IS_DONE, currentStep.PICKUP_IS_DONE),
                ...mergedStepFields(existing, currentStep),
            };
        } else {
            newSteps[newSteps.length - 1] = {
                ...existing,
                orders: mergedOrders,
                DELIVERY_IS_DONE: mergedDoneFlag(existing.DELIVERY_IS_DONE, currentStep.DELIVERY_IS_DONE),
                package_count: countPackages(existing) + countPackages(currentStep),
                ...mergedStepFields(existing, currentStep),
            };
        }
        previous = newSteps[newSteps.length - 1];
    }

    return newSteps;
}

// Unfilter steps: split steps with multiple orders into multiple steps with the same name and is_pickup, each containing a single order
export function unfilterSteps(steps) {
    if (!Array.isArray(steps)) return [];
    const result = [];
    for (const step of steps) {
        if (Array.isArray(step.orders) && step.orders.length > 1) {
            for (const order of step.orders) {
                result.push({
                    ...step,
                    orders: [order],
                });
            }
        } else {
            result.push(step);
        }
    }
    return result;
}

/**
 * Split delivery steps that bundle multiple orders so each delivery gets its own index.
 * Pickup merges (multiple orders at the same pickup stop) are preserved.
 * Per-order package counts replace the parent total so a later merge does not multiply it.
 */
export function unfilterDeliverySteps(steps) {
    if (!Array.isArray(steps)) {
        return [];
    }

    const result = [];
    for (const step of steps) {
        if (!step?.is_pickup && Array.isArray(step.orders) && step.orders.length > 1) {
            const ordersHaveCounts = step.orders.some((order) => order?.package_count != null && order.package_count !== '');
            for (const order of step.orders) {
                const nextOrder = { ...order };
                let packageCount;
                if (ordersHaveCounts) {
                    const count = Number(order?.package_count);
                    packageCount = Number.isFinite(count) && count > 0 ? count : 1;
                    nextOrder.package_count = packageCount;
                }
                result.push({
                    ...step,
                    orders: [nextOrder],
                    package_count: ordersHaveCounts ? packageCount : undefined,
                });
            }
        } else {
            result.push(step);
        }
    }

    return result;
}
